// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Table Sync Service
// Syncs floor plans and table statuses from the backend and exposes
// table actions (lock, release, instant reservation).
// ---------------------------------------------------------------------------

import { apiClient } from '@/services/api-client';
import { useTableStore } from '@/store/table-store';
import { SYNC_INTERVAL, TABLE_LOCK_DURATION } from '@/lib/constants';

// ---------------------------------------------------------------------------
// Table Sync Service
// ---------------------------------------------------------------------------

class TableSyncService {
  private syncInterval: ReturnType<typeof setInterval> | null = null;

  // ---- data loading -------------------------------------------------------

  /**
   * Load floor plans from the backend and store them in Zustand.
   */
  async loadFloorPlans(): Promise<void> {
    try {
      const response = await apiClient.getFloorPlans();
      const plans = response.floor_plans ?? [];
      useTableStore.getState().setFloorPlans(plans);
      console.log(`[TableSync] ${plans.length} floor plans loaded`);
    } catch (error) {
      console.error('[TableSync] Failed to load floor plans:', (error as Error).message);
    }
  }

  /**
   * Sync table statuses from the backend and store them in Zustand.
   */
  async syncTables(): Promise<void> {
    try {
      const response = await apiClient.getTablesStatus();
      const tablesRecord = response.tables ?? {};
      // Convert Record<string, Table> from API to Table[] for the store
      const tablesArray = Object.values(tablesRecord);
      useTableStore.getState().setTables(tablesArray);
      console.log(`[TableSync] ${tablesArray.length} tables synced`);
    } catch (error) {
      console.error('[TableSync] Failed to sync tables:', (error as Error).message);
    }
  }

  // ---- auto-sync ----------------------------------------------------------

  /**
   * Start periodic table sync.
   */
  startAutoSync(): void {
    this.stopAutoSync();

    this.syncInterval = setInterval(() => {
      this.syncTables().catch(() => {});
    }, SYNC_INTERVAL);

    console.log('[TableSync] Auto-sync started');
  }

  /**
   * Stop periodic table sync.
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // ---- table actions ------------------------------------------------------

  /**
   * Lock a table for a given duration (default: TABLE_LOCK_DURATION seconds).
   * Re-syncs tables after the action.
   */
  async lockTable(tableId: string, duration?: number): Promise<void> {
    try {
      await apiClient.lockTable(tableId, duration ?? TABLE_LOCK_DURATION);
      await this.syncTables();
      console.log(`[TableSync] Table ${tableId} locked`);
    } catch (error) {
      console.error('[TableSync] Failed to lock table:', (error as Error).message);
      throw error;
    }
  }

  /**
   * Release (unlock) a table. Re-syncs tables after the action.
   */
  async releaseTable(tableId: string): Promise<void> {
    try {
      await apiClient.releaseTable(tableId);
      await this.syncTables();
      console.log(`[TableSync] Table ${tableId} released`);
    } catch (error) {
      console.error('[TableSync] Failed to release table:', (error as Error).message);
      throw error;
    }
  }

  /**
   * Create an instant (walk-in) reservation for a table.
   * Re-syncs tables after the action.
   */
  async createInstantReservation(
    tableId: string,
    clientInfo: { name?: string; phone?: string; nb_persons?: number },
  ): Promise<void> {
    try {
      await apiClient.createInstantReservation(tableId, clientInfo);
      await this.syncTables();
      console.log(`[TableSync] Instant reservation created for table ${tableId}`);
    } catch (error) {
      console.error(
        '[TableSync] Failed to create instant reservation:',
        (error as Error).message,
      );
      throw error;
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const tableSync = new TableSyncService();
